import { useEffect, useMemo, useRef, useState } from "react";
import { ChartLine, ListBullets, X, Plus, Lightning, Wallet, Funnel, Eye, ArrowSquareOut, CaretLeft, CaretRight, CaretDown, CaretUp, Globe } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { appendToStaging } from "@/lib/stagingBuffer";
import InfoTip from "@/components/InfoTip";
import { usePublicPricing, fmt$, fmtPct } from "@/lib/usePublicPricing";

// iter-31-P2b: key-source pills in usage reports are clickable — they jump to
// the right Operations tab card so users can manage the source they're viewing.
// `Platform` → UsageOverview / Credit-Balance (platform calls eat credit).
// `BYOK`     → BYOKCard (the way to reduce Platform calls is to add BYOK).
// `BYOK-YT`  → YouTubeKeyCard (sibling card for YT-only key).
const KEY_SOURCE_TARGETS = {
  platform: { hash: "#platform-info", label: "What is the platform key?" },
  byok:     { hash: "#byok-card",     label: "Manage BYOK key" },
  "byok-yt":{ hash: "#yt-key-card",   label: "Manage YouTube key" },
};
function keySourceTargetFor(src) {
  const key = (src || "").toLowerCase().replace("_", "-");
  return KEY_SOURCE_TARGETS[key] || null;
}

const ENDPOINT_LABEL = {
  "ask": "Public Q&A",
  "compress": "Chat compression",
  "tone-architect": "Tone Architect",
  "export-summary": "Export summary",
  "workbench-run": "Workbench",
  "probe": "QA probe",
  "buddy-chat": "AI Assistant",
  "voice-chat": "Voice chat",
  "voice-stt": "Voice · speech-to-text",
  "voice-tts": "Voice · text-to-speech",
  "staging-voice-stt": "Voice · dictation",
  "yt-metadata": "YouTube · Metadata",
  "yt-transcript": "YouTube · Transcript",
  "yt-comments": "YouTube · Comments",
  "yt-library": "YouTube · Library fetch",
  // Feb 22, 2026 (Randy pt89c) — Scope A recirc additions. These two
  // are the big-output calendar surfaces whose Output cell is now
  // clickable to push back into the silo's Workbench staging.
  "chore-plan": "Smart Chore Schedule",
  "chore-master-file": "Master Chore File",
  // Iter171 (Randy pt-usage-op-labels, Feb 13 2026) — YTRE + YT
  // Replies now log with their own endpoint labels via the
  // X-Internal-Source loopback header (see server.py::/s/{slug}/ask).
  // Before this, every loopback call rolled up under "Public Q&A"
  // even though the operation origin was Randy's YouTube tools.
  "ytre": "YTRE · Reply draft",
  "ytre-suggest-range": "YTRE · Auto-suggest",
  "ytre-master-reply": "YTRE · Master Reply",
  "yt-replies": "YT Replies · Draft",
};
// Jun 2026 (Randy) — voice operations can be switched off by the creator, so we
// hint that on hover/tap in the usage report.
const VOICE_ENDPOINTS = new Set(["voice-chat", "voice-stt", "voice-tts", "staging-voice-stt"]);

const fmtUSD = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;
const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return iso; }
};

// Role label + tone for the admin breakdown table — derived_role from
// the backend wins (single source of truth via role_helpers.derive_role).
// Falls back to legacy boolean derivation only if derived_role is missing
// (e.g., during a brief deploy gap).
function roleBadgeFor(row) {
  const dr = row?.derived_role || (
    row?.role === "super_admin" ? "super_admin"
    : row?.is_affiliate ? "affiliate_creator"
    : row?.is_paid_creator ? ((row?.silo_count || 0) >= 1 ? "creator" : "new_creator")
    : "fan"
  );
  const TONES = {
    super_admin: "bg-[#FBE9E1] text-[#7A3A2A]",
    affiliate_creator: "bg-[#E7E1F5] text-[#4B3A7A]",
    creator: "bg-[#E8EFE9] text-[#3A4F40]",
    new_creator: "bg-[#F4EEDC] text-[#6B5A2D]",
    fan: "bg-[#F0F1EE] text-[#6B6A68] border hairline",
  };
  const LABELS = {
    super_admin: "Super Admin",
    affiliate_creator: "Creator Pro",
    creator: "Creator",
    new_creator: "New Creator",
    fan: "Fan",
  };
  return { label: LABELS[dr] || dr, cls: TONES[dr] || TONES.fan };
}

// Jun 2026 — "Asked by" badge. Maps the asker's derived role to a label + tone
// for the usage report's attribution column. "anonymous" is the only non-account
// value (a visitor with no login). Billing always lands on the silo OWNER — this
// column is visibility into WHO consumed each answer.
function askerBadgeFor(role) {
  const r = (role || "").toLowerCase();
  const TONES = {
    super_admin: "bg-[#FBE9E1] text-[#7A3A2A]",
    affiliate_creator: "bg-[#E7E1F5] text-[#4B3A7A]",
    creator: "bg-[#E8EFE9] text-[#3A4F40]",
    new_creator: "bg-[#F4EEDC] text-[#6B5A2D]",
    subscriber: "bg-[#E4EEF7] text-[#2F5A7A]",
    fan: "bg-[#E8EEF5] text-[#3A4A6B]",
    anonymous: "bg-[#F0F1EE] text-[#6B6A68] border hairline",
  };
  const LABELS = {
    super_admin: "Super Admin",
    affiliate_creator: "Creator Pro",
    creator: "Creator",
    new_creator: "New Creator",
    subscriber: "Subscriber",
    fan: "Fan",
    anonymous: "Anonymous",
  };
  return { label: LABELS[r] || r || "—", cls: TONES[r] || TONES.anonymous };
}

const RANGE_PRESETS = [
  { id: "7", label: "Last 7 days", days: 7 },
  { id: "30", label: "Last 30 days", days: 30 },
  { id: "90", label: "Last 90 days", days: 90 },
];

function isoDaysAgo(days) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString();
}

export default function UsageOverview({ scope = "me", onTopUp, siloFilter = null, onRecallToStaging = null, hideCreditCard = false }) {
  // scope: "me" (creator/own) | "admin" (super admin sees all)
  // siloFilter: when set, restrict all queries + report to this silo id (per-silo Operations tab).
  // onRecallToStaging: when set + scope==="me", the Output column becomes a clickable link in the
  // report that hands the snapshot back here (the silo's Operations tab wires this to the Workbench staging).
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reportOpen, setReportOpen] = useState(false);
  const [rangeId, setRangeId] = useState("7");
  const days = useMemo(() => RANGE_PRESETS.find((r) => r.id === rangeId)?.days || 7, [rangeId]);

  const overviewUrl = scope === "admin" ? "/admin/usage/overview" : "/usage/me";

  const load = async (d = days) => {
    setLoading(true);
    try {
      const fromIso = isoDaysAgo(d);
      const params = { from_: fromIso };
      if (siloFilter) params.silo_id = siloFilter;
      const r = await api.get(overviewUrl, { params });
      setOverview(r.data);
    } catch { /* silent — unauthorized or no data */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(days); /* eslint-disable-next-line */ }, [days, scope, siloFilter]);

  const sortedEndpoints = useMemo(() => {
    if (!overview?.by_endpoint) return [];
    return Object.entries(overview.by_endpoint)
      .map(([k, v]) => [k, v])
      .sort((a, b) => b[1] - a[1]);
  }, [overview]);

  const lowBalance = (overview?.credit_balance_cents ?? 0) > 0 && (overview.credit_balance_cents < 1000);
  const noBalance = (overview?.credit_balance_cents ?? 0) <= 0;

  return (
    <div id="credit-balance" className="bg-white border hairline rounded-xl p-6 scroll-mt-28" data-testid={`usage-overview-${scope}`}>
      <div className="flex items-start gap-3 flex-wrap">
        <div className="w-10 h-10 rounded-md bg-[#F0F1EE] text-[#4A5D4E] flex items-center justify-center shrink-0">
          <ChartLine size={20} weight="duotone" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="text-sm font-medium text-[#1C1B1A]">Usage overview</div>
              <div className="text-xs text-[#6B6A68] mt-0.5">
                {scope === "admin" ? "Platform-wide consumption across all creators." : "Your API consumption — calls billed against your balance."}
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              {RANGE_PRESETS.map((r) => (
                <button
                  key={r.id}
                  onClick={() => setRangeId(r.id)}
                  data-testid={`usage-range-${r.id}`}
                  className={`text-xs px-2.5 py-1.5 rounded-md border transition-colors ${rangeId === r.id ? "border-[#4A5D4E] bg-[#F0F1EE] text-[#1C1B1A]" : "hairline text-[#6B6A68] hover:border-[#4A5D4E]/40"}`}
                >{r.label}</button>
              ))}
            </div>
          </div>

          <div className="mt-5 grid grid-cols-2 lg:grid-cols-4 gap-4">
            {scope === "me" && overview?.has_byok ? (
              <div data-testid="usage-stat-byok-billing">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#6B6A68] flex items-center gap-1"><Wallet size={11} weight="duotone" /> Billing</div>
                <div className="font-serif-display text-2xl mt-1 text-[#4A5D4E]">BYOK</div>
                <div className="mt-1 flex items-center gap-2 flex-wrap">
                  <a
                    href="https://aistudio.google.com/app/billing"
                    target="_blank"
                    rel="noopener noreferrer external"
                    referrerPolicy="no-referrer"
                    data-testid="byok-google-billing-link"
                    className="text-[11px] text-[#4A5D4E] hover:underline inline-flex items-center gap-1"
                  >
                    Open Google billing ↗
                  </a>
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText("https://aistudio.google.com/app/billing");
                        toast.success("Google billing URL copied — paste into a new tab.");
                      } catch {
                        toast.error("Clipboard blocked — URL: https://aistudio.google.com/app/billing");
                      }
                    }}
                    data-testid="byok-google-billing-copy"
                    className="text-[10px] uppercase tracking-[0.15em] px-1.5 py-0.5 rounded border hairline text-[#6B6A68] hover:text-[#4A5D4E] hover:border-[#4A5D4E]"
                    title="Some preview browsers block _blank — copy the URL and paste in a fresh tab."
                  >
                    Copy URL
                  </button>
                </div>
              </div>
            ) : (
              <div data-testid="usage-stat-balance">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#6B6A68] flex items-center gap-1"><Wallet size={11} weight="duotone" /> {scope === "admin" ? "Prepaid credit" : "Platform credits"}</div>
                <div className={`font-serif-display text-2xl mt-1 ${lowBalance ? "text-[#C16E5A]" : "text-[#1C1B1A]"}`}>
                  {loading ? "…" : fmtUSD(overview?.credit_balance_cents)}
                </div>
                {scope === "admin" && (
                  <div className="text-[10px] text-[#6B6A68] mt-1">Sum of every wallet · not lifetime activity</div>
                )}
                {scope === "me" && noBalance && (
                  <div className="text-[11px] text-[#6B6A68] mt-1">No credits — use the Key balance tracker above to top up.</div>
                )}
                {scope === "me" && lowBalance && (
                  <div className="text-[11px] text-[#C16E5A] mt-1">Below $10 — alert sent.</div>
                )}
              </div>
            )}
            <div data-testid="usage-stat-cost">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#6B6A68] flex items-center gap-1"><Lightning size={11} weight="duotone" /> {`${days}-day cost`}</div>
              <div className="font-serif-display text-2xl mt-1 text-[#1C1B1A]">
                {loading ? "…" : fmtUSD(overview?.total_cost_cents)}
              </div>
            </div>
            <div data-testid="usage-stat-calls">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#6B6A68]">{`${days}-day calls`}</div>
              <div className="font-serif-display text-2xl mt-1 text-[#1C1B1A]">{loading ? "…" : (overview?.total_calls ?? 0).toLocaleString()}</div>
            </div>
            <div className="flex items-end justify-end gap-2">
              {/* Feb 21, 2026 — Top-up button removed. Top-up lives in
                  the Key balance tracker above (single source of truth).
                  This panel is now read-only telemetry. */}
              <button data-testid="usage-view-report-btn" onClick={() => setReportOpen(true)}
                className="text-xs px-3 py-2 rounded-md border hairline bg-white hover:border-[#4A5D4E] hover:text-[#4A5D4E] flex items-center gap-1.5">
                <ListBullets size={12} /> View report
              </button>
            </div>
          </div>

          {sortedEndpoints.length > 0 && !loading && (
            <CollapsibleEndpoints
              scope={scope}
              rows={sortedEndpoints}
              endpointLabel={ENDPOINT_LABEL}
              fmtUSD={fmtUSD}
            />
          )}
          {scope === "me" && overview && !overview.has_byok && (
            <p className="text-[11px] text-[#6B6A68] mt-3">Using the platform API key. <strong>Bring your own key</strong> below to bill against your own provider account.</p>
          )}
        </div>
      </div>

      {reportOpen && <ReportModal scope={scope} days={days} onClose={() => setReportOpen(false)} siloFilter={siloFilter} onRecallToStaging={onRecallToStaging} />}
      {scope === "admin" && null/* AdminMarginTracker moved to Financials tab (Jun 2026, Randy) */}
      {scope === "admin" && <AdminCreatorBreakdown days={days} />}
    </div>
  );
}

// Feb 19, 2026 (Randy Photo #5) — Per-endpoint pill list is COLLAPSED
// BY DEFAULT everywhere it renders (Super Admin Overview, Financials,
// per-creator breakdowns). Randy: "This list is getting obnoxiously long."
// Preference persists across sessions per-user via localStorage.
function CollapsibleEndpoints({ scope, rows, endpointLabel, fmtUSD }) {
  const key = `mysilo_usage_endpoints_open_${scope || "me"}`;
  const [open, setOpen] = useState(() => {
    try { return localStorage.getItem(key) === "1"; } catch { return false; }
  });
  const persistOpen = (v) => {
    setOpen(v);
    try { localStorage.setItem(key, v ? "1" : "0"); } catch { /* ignore */ }
  };
  const totalCents = rows.reduce((s, [, c]) => s + (c || 0), 0);
  return (
    <div className="mt-4" data-testid={`usage-endpoints-collapsible-${scope || "me"}`}>
      <button
        type="button"
        onClick={() => persistOpen(!open)}
        data-testid={`usage-endpoints-toggle-${scope || "me"}`}
        className="w-full flex items-center gap-2 text-[11px] px-3 py-2 rounded-md bg-[#F0F1EE] hover:bg-[#E5E5E0] text-[#1C1B1A] transition-colors"
        aria-expanded={open}
      >
        <ListBullets size={12} weight="duotone" className="text-[#4A5D4E]" />
        <span className="flex-1 text-left">
          Per-endpoint breakdown — {rows.length} {rows.length === 1 ? "endpoint" : "endpoints"} · {fmtUSD(totalCents)}
        </span>
        {open ? <CaretUp size={11} weight="bold" /> : <CaretDown size={11} weight="bold" />}
      </button>
      {open && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {rows.map(([ep, cents]) => (
            <span key={ep} className="text-[11px] px-2.5 py-1 rounded-full bg-[#F0F1EE] text-[#1C1B1A]" data-testid={`usage-endpoint-${ep}`}>
              {endpointLabel[ep] || ep} · {fmtUSD(cents)}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// Jun 2026 (Randy) — provider rows for the margin tracker. The provider comes
// from each usage row's model_name. "Platform Key" is a key_source, NOT a
// provider — so billable platform-key spend is what the revenue/cost/margin
// columns MEASURE, never a fourth row. Creator BYOK rides as a sub-line.
const MARGIN_PROVIDER_LABEL = {
  gemini: "Gemini", anthropic: "Anthropic", openai: "OpenAI", other: "Unattributed",
};
const MARGIN_PROVIDER_DOT = {
  gemini: "#4A5D4E", anthropic: "#C16E5A", openai: "#3A4A3E", other: "#6B6A68",
};

export function AdminMarginTracker({ days = 30 }) {
  // Feb 27, 2026 — Sits directly below the admin usage report. Shows the
  // spread between what creators were charged vs what the platform actually
  // paid provider invoices over the same window. The "margin" line is the
  // platform's gross profit from the 3× markup on top of raw provider cost.
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const fromIso = isoDaysAgo(days);
        const r = await api.get("/admin/usage/margin", { params: { from_: fromIso } });
        if (!cancelled) setData(r.data);
      } catch { /* silent — non-critical */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [days]);

  if (loading) return null;
  if (!data) return null;
  return (
    <div className="mt-4 bg-white border hairline rounded-lg p-4" data-testid="admin-margin-tracker">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68]">Margin tracker · last {days} days</div>
          <h3 className="font-serif-display text-lg text-[#1C1B1A] mt-0.5">Platform LLM margin</h3>
          <p className="text-[11px] text-[#6B6A68] mt-1">What creators paid vs what providers invoiced — the spread is the platform's gross LLM profit. Billable platform-key usage only.</p>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="bg-[#FDF1ED] border border-[#C16E5A] rounded-md p-3" data-testid="margin-user-cost">
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#C16E5A]">Creators charged</div>
          <div className="font-serif-display text-2xl text-[#7A2A20] mt-1">{fmtUSD(data.user_cost_cents)}</div>
          <div className="text-[10px] text-[#6B6A68] mt-1">at {data.markup_x}× markup · billable only</div>
        </div>
        <div className="bg-[#E8EBE5] border border-[#4A5D4E] rounded-md p-3" data-testid="margin-raw-cost">
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#4A5D4E]">Provider invoice (your cost)</div>
          <div className="font-serif-display text-2xl text-[#2F6B3A] mt-1">{fmtUSD(data.raw_cost_cents)}</div>
          <div className="text-[10px] text-[#6B6A68] mt-1">
            {(data.billable_calls ?? data.calls).toLocaleString()} billable of {data.calls.toLocaleString()} calls
          </div>
        </div>
        <div className="bg-[#FBE9E1] border border-[#7A5200] rounded-md p-3" data-testid="margin-spread">
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#7A5200]">Platform margin</div>
          <div className="font-serif-display text-2xl text-[#7A5200] mt-1">{fmtUSD(data.margin_cents)}</div>
          <div className="text-[10px] text-[#6B6A68] mt-1">
            {data.margin_pct == null ? "profit on LLM markup" : `${data.margin_pct}% profit on LLM markup`}
          </div>
        </div>
      </div>

      {(data.byok_cents || 0) > 0 && (
        <div className="mt-2 text-[10px] text-[#6B6A68] italic" data-testid="margin-byok-note">
          Excluded above · {fmtUSD(data.byok_cents)} of creator-funded BYOK spend across{" "}
          {(data.byok_calls || 0).toLocaleString()} calls — those creators paid their provider directly, so it&apos;s
          neither platform revenue nor platform cost.
        </div>
      )}

      {(data.by_provider || []).length > 0 && (
        <div className="mt-4 border-t hairline pt-3" data-testid="margin-by-provider">
          <div className="flex items-baseline gap-2 flex-wrap mb-2">
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68]">
              Per provider · billable platform-key usage
            </div>
            <span className="ml-auto text-[10px] text-[#6B6A68] italic">
              creator BYOK listed separately — never counted as platform revenue or cost
            </span>
          </div>
          <div className="border hairline rounded-md overflow-hidden">
            <div className="px-3 py-1.5 flex items-center gap-2 text-[9px] uppercase tracking-[0.15em] text-[#6B6A68] bg-[#F7F6F1] border-b hairline">
              <span className="flex-1">Provider</span>
              <span className="w-20 text-right">Revenue</span>
              <span className="w-20 text-right">Est. cost</span>
              <span className="w-20 text-right">Margin</span>
              <span className="w-14 text-right">Margin %</span>
            </div>
            {data.by_provider.map((p) => (
              <div key={p.provider} className="border-b hairline last:border-b-0" data-testid={`margin-provider-${p.provider}`}>
                <div className="px-3 py-2 flex items-center gap-2 text-[11px]">
                  <span className="w-2 h-2 rounded-full shrink-0" style={{ background: MARGIN_PROVIDER_DOT[p.provider] || "#6B6A68" }} />
                  <span className="flex-1 min-w-0 text-[#1C1B1A] truncate">
                    {MARGIN_PROVIDER_LABEL[p.provider] || p.provider}
                    <span className="text-[#6B6A68] ml-1.5">
                      · {(p.billable_calls || 0).toLocaleString()} billable of {(p.calls || 0).toLocaleString()} calls
                    </span>
                  </span>
                  <span className="w-20 text-right font-mono tabular-nums text-[#7A2A20]">{fmtUSD(p.revenue_cents)}</span>
                  <span className="w-20 text-right font-mono tabular-nums text-[#2F6B3A]">{fmtUSD(p.cost_cents)}</span>
                  <span className="w-20 text-right font-mono tabular-nums text-[#7A5200]">{fmtUSD(p.margin_cents)}</span>
                  <span className="w-14 text-right font-mono tabular-nums text-[#6B6A68]" data-testid={`margin-provider-pct-${p.provider}`}>
                    {p.margin_pct == null ? "—" : `${p.margin_pct}%`}
                  </span>
                </div>
                {(p.byok_cents || 0) > 0 && (
                  <div className="px-3 pb-2 flex items-center gap-1.5 text-[10px] text-[#6B6A68]" data-testid={`margin-provider-byok-${p.provider}`}>
                    <span className="opacity-50 ml-3">└</span>
                    <span>creator-funded BYOK · {(p.byok_calls || 0).toLocaleString()} calls ·</span>
                    <span className="font-mono tabular-nums">{fmtUSD(p.byok_cents)}</span>
                    <span className="italic opacity-70">not billed by us</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Oct 2026 (Randy) — TWO strips, because the old six "audience" pills quietly
// mixed two different questions and the caption under them claimed a nesting
// the code never implemented. Row 1 is the audience ON a creator's silos
// (fans_total is a genuine total of the three beside it). Row 2 is the referral
// tree — accounts attached to their invite link, bucketed by the SAME badge the
// Role column paints, plus "Earning credit": the subset the money engine
// recognises. A comped Creator Pro shows in Creator Pros and NOT in Earning,
// which is exactly why the badge and the old pill disagreed.
const AUDIENCE_PILLS = [
  { key: "anonymous", label: "Anonymous", sub: "visitors", cls: "bg-[#F0F1EE] border-[#E2E0DA] text-[#6B6A68]" },
  { key: "fans_total", label: "Fans (total)", sub: "the three to the right", cls: "bg-[#FBF6E9] border-[#EAD9A8] text-[#6B5A2D]" },
  { key: "interactions", label: "Engaged", sub: "asked · starred", cls: "bg-[#FBF6E9] border-[#EAD9A8] text-[#6B5A2D]" },
  { key: "non_paying", label: "Free followers", sub: "followed at $0", cls: "bg-[#FCEEE9] border-[#EAC4B5] text-[#A6553E]" },
  { key: "paying", label: "Paying", sub: "active charges", cls: "bg-[#E8EEF5] border-[#BFD0E5] text-[#3A4A6B]" },
];

const REFERRAL_PILLS = [
  { key: "joined", label: "Joined", sub: "the three to the right", cls: "bg-[#F0F1EE] border-[#E2E0DA] text-[#4A4A48]" },
  { key: "not_creators", label: "Not creators yet", sub: "still fans", cls: "bg-[#FBF6E9] border-[#EAD9A8] text-[#6B5A2D]" },
  { key: "creators", label: "Creators", sub: "paid or comped", cls: "bg-[#E8EFE9] border-[#BBD2BE] text-[#3A4F40]" },
  { key: "creator_pros", label: "Creator Pros", sub: "badge in the table", cls: "bg-[#E7E1F5] border-[#C9BCE8] text-[#4B3A7A]" },
  { key: "earning", label: "Earning credit", sub: "referral awarded", cls: "bg-[#E8EEF5] border-[#BFD0E5] text-[#3A4A6B]" },
];

function AdminCreatorBreakdown({ days }) {
  const pricing = usePublicPricing();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [sortKey, setSortKey] = useState("recurring_desc");
  // Jun 2026 (Randy) — per-creator audience drilldown. Click a row to mirror
  // that creator's dashboard "Your audience" pills below the table.
  const [selected, setSelected] = useState(null);
  const [audience, setAudience] = useState(null);
  const [audLoading, setAudLoading] = useState(false);
  // Oct 2026 (Randy) — three-state card press: 1st highlights those rows in the
  // table, 2nd narrows the table to only them, 3rd clears. `{ key, stage }`.
  const [pill, setPill] = useState(null);
  const tableRef = useRef(null);

  const pressPill = (key) => {
    setPage(1);
    setPill((cur) => {
      if (!cur || cur.key !== key) return { key, stage: 1 };
      if (cur.stage === 1) {
        // Stage 2 shrinks the table, which would otherwise collapse out of
        // view while the eye is still down on the cards.
        setTimeout(() => tableRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
        return { key, stage: 2 };
      }
      return null;
    });
  };

  const pillIds = useMemo(() => {
    if (!pill || !audience) return null;
    return new Set(audience.member_ids?.[pill.key] || []);
  }, [pill, audience]);

  const selectCreator = async (r) => {
    setSelected(r);
    setAudience(null);
    setPill(null);
    setAudLoading(true);
    try {
      const res = await api.get(`/admin/users/${r.user_id}/audience`);
      setAudience(res.data);
    } catch { toast.error("Failed to load that creator's audience"); }
    finally { setAudLoading(false); }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const fromIso = isoDaysAgo(days);
        const r = await api.get("/admin/usage/by-creator", { params: { from_: fromIso } });
        if (!cancelled) setRows(r.data || []);
      } catch { if (!cancelled) toast.error("Failed to load creator breakdown"); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [days]);

  const filtered = useMemo(() => {
    let out = rows;
    if (filter) {
      const q = filter.toLowerCase();
      out = out.filter((r) => (r.email || "").toLowerCase().includes(q) || (r.name || "").toLowerCase().includes(q));
    }
    const sorted = [...out];
    // Feb 21, 2026 — sort keys now reflect the affiliate-shaped report.
    // Cost/Calls/Balance moved out (LLM telemetry lives in the Usage report
    // modal). Default = recurring incoming ↓ so the biggest paying-base
    // creators surface at top.
    if (sortKey === "recurring_desc") sorted.sort((a, b) => (b.recurring_incoming_cents || 0) - (a.recurring_incoming_cents || 0));
    else if (sortKey === "gate_desc") sorted.sort((a, b) => ((b.affiliate_gate_lifetime_cents || 0) + (b.affiliate_gate_potential_cents || 0)) - ((a.affiliate_gate_lifetime_cents || 0) + (a.affiliate_gate_potential_cents || 0)));
    else if (sortKey === "silo_desc") sorted.sort((a, b) => (b.silo_earnings_lifetime_cents || 0) - (a.silo_earnings_lifetime_cents || 0));
    else if (sortKey === "creators_desc") sorted.sort((a, b) => (b.creators_count || 0) - (a.creators_count || 0));
    else if (sortKey === "email_asc") sorted.sort((a, b) => (a.email || "").localeCompare(b.email || ""));
    return sorted;
  }, [rows, filter, sortKey]);

  // Feb 21, 2026 — totals strip rewritten to match the new column set.
  // recurring = live $/mo across affiliates only (potentials are projections,
  // not platform liability). gate_lifetime = total $ paid out to affiliates
  // historically. silo_lifetime = total $ pocketed by creators from fans.
  // creators = total paid-creator nodes downstream across all referrers.
  const totals = useMemo(() => filtered.reduce(
    (acc, r) => ({
      recurring: acc.recurring + (r.is_affiliate ? (r.recurring_incoming_cents || 0) : 0),
      gate_lifetime: acc.gate_lifetime + (r.affiliate_gate_lifetime_cents || 0),
      silo_lifetime: acc.silo_lifetime + (r.silo_earnings_lifetime_cents || 0),
      creators: acc.creators + (r.creators_count || 0),
    }), { recurring: 0, gate_lifetime: 0, silo_lifetime: 0, creators: 0 },
  ), [filtered]);

  // Jun 2026 (Randy) — the breakdown includes anyone in a referral base, which
  // can be FANS (not just creators). Count the two groups separately so the
  // subtitle doesn't mislabel every row as a "creator".
  const roleSplit = useMemo(() => {
    let creators = 0, fans = 0;
    for (const r of filtered) {
      const dr = r?.derived_role || (
        r?.role === "super_admin" ? "super_admin"
        : r?.is_affiliate ? "affiliate_creator"
        : r?.is_paid_creator ? "creator"
        : "fan"
      );
      if (dr === "fan") fans += 1; else creators += 1;
    }
    return { creators, fans };
  }, [filtered]);

  // May 31, 2026 — paginate the rendered rows (the report computes the full
  // ranked list, but we only paint 50 at a time so the table stays snappy
  // past thousands of creators). Totals above stay over the FULL filtered set.
  const PAGE_SIZE = 50;
  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [filter, sortKey, days]);
  // Oct 2026 — stage 2 of a card press narrows the table to that card's members.
  const visible = useMemo(() => (
    pill?.stage === 2 && pillIds ? filtered.filter((r) => pillIds.has(r.user_id)) : filtered
  ), [filtered, pill, pillIds]);
  // Jun 2026 — auto-select the top creator so the audience pills show up right
  // away (Randy reported them "not showing" — they were gated behind a manual
  // row click). Only fires while nothing is selected yet, so a manual "Clear"
  // sticks until the list changes.
  useEffect(() => {
    if (!selected && filtered.length > 0) selectCreator(filtered[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered]);
  const totalPages = Math.max(1, Math.ceil(visible.length / PAGE_SIZE));
  const pageRows = visible.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div className="mt-6" data-testid="admin-creator-breakdown">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
        <div>
          <div className="text-sm font-medium text-[#1C1B1A]">Per-creator breakdown</div>
          <div className="text-xs text-[#6B6A68]">
            {roleSplit.creators} creator{roleSplit.creators === 1 ? "" : "s"}{roleSplit.fans > 0 ? ` · ${roleSplit.fans} fan${roleSplit.fans === 1 ? "" : "s"}` : ""} · {fmtUSD(totals.recurring)}/mo live recurring · {fmtUSD(totals.gate_lifetime)} paid-out gates · {fmtUSD(totals.silo_lifetime)} silo lifetime · {totals.creators.toLocaleString()} paying downstream
          </div>
        </div>
        <div className="flex items-center gap-2">
          <input data-testid="admin-creator-filter" value={filter} onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter by email/name…"
            className="text-xs bg-white border hairline rounded-md px-3 py-1.5 w-48" />
          <select data-testid="admin-creator-sort" value={sortKey} onChange={(e) => setSortKey(e.target.value)}
            className="text-xs bg-white border hairline rounded-md px-3 py-1.5">
            <option value="recurring_desc">Sort · recurring ↓</option>
            <option value="gate_desc">Sort · Creator Pro gate ↓</option>
            <option value="silo_desc">Sort · silo earnings ↓</option>
            <option value="creators_desc">Sort · creators ↓</option>
            <option value="email_asc">Sort · email A→Z</option>
          </select>
        </div>
      </div>
      {pill?.stage === 2 && (
        <div className="mb-2 flex items-center justify-between gap-2 rounded-md border border-[#EAD9A8] bg-[#FBF6E9] px-3 py-2 text-xs text-[#6B5A2D]"
          data-testid="admin-creator-pill-filter-notice">
          <span>
            Showing only <span className="font-medium">{[...AUDIENCE_PILLS, ...REFERRAL_PILLS].find((p) => p.key === pill.key)?.label}</span>
            {" "}for {selected?.email || "this creator"} · {visible.length} of {filtered.length} rows
          </span>
          <button type="button" onClick={() => setPill(null)} data-testid="admin-creator-pill-filter-clear"
            className="text-[10px] uppercase tracking-[0.15em] hover:text-[#1C1B1A] transition-colors whitespace-nowrap">
            Show all
          </button>
        </div>
      )}
      <div ref={tableRef} className="bg-white border hairline rounded-lg overflow-x-auto">
        {loading ? (
          <div className="p-8 text-center text-sm text-[#6B6A68]">Loading…</div>
        ) : visible.length === 0 ? (
          <div className="p-8 text-center text-sm text-[#6B6A68]" data-testid="admin-creator-empty">
            {pill?.stage === 2 ? "Nobody in that group has a row here." : "No creators match."}
          </div>
        ) : (
          <table className="w-full text-sm min-w-[1080px]" data-testid="admin-creator-table">
            <thead className="bg-[#FDFCF8] text-[10px] uppercase tracking-[0.18em] text-[#6B6A68]">
              <tr>
                <th className="text-left px-4 py-2.5 font-medium">Creator</th>
                <th className="text-left px-4 py-2.5 font-medium">Role</th>
                {/* Feb 21, 2026 — "Referred by" between Role and the
                    affiliate-money columns. Empty = organic signup. */}
                <th className="text-left px-4 py-2.5 font-medium">Referred by</th>
                {/* Feb 21, 2026 — unified affiliate-shaped report.
                    For affiliates these cells show LIVE $.
                    For below-affiliate they show muted/italic projections
                    ("would-earn" if they upgraded today). */}
                <th className="text-right px-4 py-2.5 font-medium">
                  <span className="inline-flex items-center justify-end gap-1 cursor-help">
                    Recurring incoming
                    {/* Feb 21 (eve): placement flipped to bottom-center so the
                        tooltip hangs DOWN over the table instead of UP off
                        the top of the viewport (where the auto-flip was
                        clipping it and the body never showed). */}
                    <InfoTip placement="bottom-center" width={280} label="What recurring incoming means">
                      <strong>Creator Pros:</strong> live $/mo from active paying referees ({fmtPct(pricing.affiliate_base_rev_share_pct)} of each {fmt$(pricing.platform_fee_cents)}/mo platform fee).{"\n\n"}
                      <strong>Below Creator Pro:</strong> muted projection of what they’d earn if they activated today.
                    </InfoTip>
                  </span>
                </th>
                <th className="text-right px-4 py-2.5 font-medium">
                  <span className="inline-flex items-center justify-end gap-1 cursor-help">
                    Creator Pro gate
                    <InfoTip placement="bottom-center" width={280} label="What Creator Pro gate means">
                      <strong>Creator Pro:</strong> lifetime $ banked from retro gates (when referred creators upgraded to Creator Pro and you earned {fmtPct(pricing.affiliate_base_rev_share_pct)} of their {fmt$(pricing.day_one_bundle_discount_threshold_cents)} entry).{"\n\n"}
                      <strong>Below Creator Pro:</strong> sum they could collect if they upgrade today (in-window referred creators only).
                    </InfoTip>
                  </span>
                </th>
                <th className="text-right px-4 py-2.5 font-medium">Silo earnings</th>
                <th className="text-right px-4 py-2.5 font-medium">Creators</th>
                <th className="text-right px-4 py-2.5 font-medium">
                  <span className="inline-flex items-center justify-end gap-1 cursor-help">
                    Time left
                    <InfoTip placement="bottom-center" width={280} label="What time left means">
                      Shows the shortest open 30-day retro window across any of this creator’s <strong>Creator Pro</strong> referees.{"\n\n"}
                      It’s the clock to activate themselves and capture those retro gate payouts.{"\n\n"}
                      “—” means either they’re already Creator Pro (the clock stops once activated) OR none of their referees has upgraded to Creator Pro yet.
                    </InfoTip>
                  </span>
                </th>
                <th className="px-4 py-2.5"></th>
              </tr>
            </thead>
            <tbody className="divide-y hairline">
              {pageRows.map((r) => (
                <tr key={r.user_id}
                  onClick={() => selectCreator(r)}
                  className={`cursor-pointer hover:bg-[#FDFCF8] transition-colors ${
                    pillIds?.has(r.user_id) ? "bg-[#FDE68A] ring-2 ring-inset ring-[#C16E5A] font-medium"
                    : selected?.user_id === r.user_id ? "bg-[#F0F1EE]" : ""
                  }`}
                  data-pill-match={pillIds?.has(r.user_id) ? "1" : "0"}
                  data-testid={`admin-creator-row-${r.user_id}`}>
                  <td className="px-4 py-2.5">
                    <div className="text-[#1C1B1A]">{r.email || "—"}</div>
                    <div className="text-[11px] text-[#6B6A68]">{r.name || ""}</div>
                  </td>
                  <td className="px-4 py-2.5">
                    {(() => {
                      const b = roleBadgeFor(r);
                      return (
                        <span
                          data-testid={`admin-creator-role-${r.user_id}`}
                          className={`text-[10px] uppercase tracking-[0.15em] px-1.5 py-0.5 rounded whitespace-nowrap ${b.cls}`}
                        >
                          {b.label}
                        </span>
                      );
                    })()}
                  </td>
                  {/* Feb 21, 2026 — Referred by cell. Renders the upline
                      creator's name (preferred) or local-part of their
                      email. Em-dash for organic signups. Title attribute
                      shows the full email on hover for quick verification. */}
                  <td className="px-4 py-2.5" data-testid={`admin-creator-referrer-${r.user_id}`}>
                    {r.referrer_email ? (
                      <span className="text-[12px] text-[#1C1B1A]" title={r.referrer_email}>
                        {r.referrer_name || r.referrer_email.split("@")[0]}
                      </span>
                    ) : (
                      <span className="text-[#6B6A68]">—</span>
                    )}
                  </td>
                  {/* Feb 21, 2026 — Recurring incoming.
                      Affiliate row: solid $/mo (live).
                      Non-affiliate: muted italic "Potential: $X/mo".
                      Zero in either case: em-dash. */}
                  <td className="px-4 py-2.5 text-right tabular-nums" data-testid={`admin-creator-recurring-${r.user_id}`}>
                    {(r.recurring_incoming_cents || 0) === 0 ? (
                      <span className="text-[#6B6A68]">—</span>
                    ) : r.is_affiliate ? (
                      <span className="text-[#1C1B1A] font-medium">{fmtUSD(r.recurring_incoming_cents)}<span className="text-[10px] text-[#6B6A68]">/mo</span></span>
                    ) : (
                      <span className="text-[#6B6A68] italic text-[11px]">Potential: {fmtUSD(r.recurring_incoming_cents)}/mo</span>
                    )}
                  </td>
                  {/* Affiliate gate cell.
                      Affiliate: lifetime $ banked (solid).
                      Non-affiliate: italic muted "Potential: $X" of in-window retros. */}
                  <td className="px-4 py-2.5 text-right tabular-nums" data-testid={`admin-creator-gate-${r.user_id}`}>
                    {r.is_affiliate ? (
                      (r.affiliate_gate_lifetime_cents || 0) > 0 ? (
                        <span className="text-[#1C1B1A] font-medium">{fmtUSD(r.affiliate_gate_lifetime_cents)}</span>
                      ) : (
                        <span className="text-[#6B6A68]">—</span>
                      )
                    ) : (
                      (r.affiliate_gate_potential_cents || 0) > 0 ? (
                        <span className="text-[#6B6A68] italic text-[11px]">Potential: {fmtUSD(r.affiliate_gate_potential_cents)}</span>
                      ) : (
                        <span className="text-[#6B6A68]">—</span>
                      )
                    )}
                  </td>
                  {/* Silo earnings — lifetime fan-sub + tip $ actually KEPT by
                      the creator. Sep 25, 2026 (F4): read through the shared
                      creator-net helper, so a Model-A charge counts in full
                      (the supporter paid our fee on top) while a legacy charge
                      still nets 95%. Same column semantic for everyone
                      (affiliate or not — non-affiliates can still own silos). */}
                  <td className="px-4 py-2.5 text-right tabular-nums" data-testid={`admin-creator-silo-${r.user_id}`}>
                    {(r.silo_earnings_lifetime_cents || 0) > 0 ? (
                      <span className="text-[#1C1B1A]">{fmtUSD(r.silo_earnings_lifetime_cents)}</span>
                    ) : (
                      <span className="text-[#6B6A68]">—</span>
                    )}
                  </td>
                  {/* Count of paid creators in the referral base.
                      Feb 21 (eve): italic+muted for non-affiliates to match
                      the "Potential" treatment on Recurring / Affiliate gate
                      — these aren't earning anything from those creators
                      until they activate. */}
                  <td className="px-4 py-2.5 text-right tabular-nums" data-testid={`admin-creator-count-${r.user_id}`}>
                    {(r.creators_count || 0) > 0 ? (
                      r.is_affiliate ? (
                        <span className="text-[#1C1B1A]">{r.creators_count}</span>
                      ) : (
                        <span className="text-[#6B6A68] italic text-[11px]">{r.creators_count}</span>
                      )
                    ) : (
                      <span className="text-[#6B6A68]">—</span>
                    )}
                  </td>
                  {/* Time left.
                      Affiliate: "—" (clock concept doesn't apply once
                      activated — Randy's call).
                      Non-affiliate: shortest retro window across in-window
                      referees, or em-dash if none open. */}
                  <td className="px-4 py-2.5 text-right tabular-nums" data-testid={`admin-creator-timeleft-${r.user_id}`}>
                    {r.is_affiliate ? (
                      <span className="text-[#6B6A68]">—</span>
                    ) : r.time_left_days != null && r.time_left_days > 0 ? (
                      <span className={`text-[11px] ${r.time_left_days <= 7 ? "text-[#C16E5A] font-medium" : "text-[#1C1B1A]"}`}>
                        {r.time_left_days}d
                      </span>
                    ) : (
                      <span className="text-[#6B6A68]">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    {(r.role !== "super_admin" || r.is_affiliate) && (
                      <div className="inline-flex items-center gap-1.5">
                      {r.is_affiliate && (
                        <button
                          type="button"
                          onClick={async (e) => {
                            e.stopPropagation();
                            try {
                              const res = await api.post(`/admin/test/payout-ready`, { user_id: r.user_id, enabled: true });
                              const cents = res.data?.transferred_cents || 0;
                              toast.success(cents > 0
                                ? `Test payout sent: ${fmtUSD(cents)} marked paid (simulated).`
                                : `Marked payout-ready — no payable balance right now.`);
                            } catch (e) {
                              toast.error(e?.response?.data?.detail || "Test payout failed.");
                            }
                          }}
                          data-testid={`test-payout-${r.user_id}`}
                          className="text-[10px] uppercase tracking-[0.15em] px-2 py-1 rounded border hairline text-[#6B5A2D] hover:border-[#6B5A2D] hover:bg-[#F4EEDC]"
                          title="TEST ONLY: simulate a connected Stripe payout account and pay out any held balance now (no real onboarding)."
                        >
                          Payout (test)
                        </button>
                      )}
                      {r.role !== "super_admin" && (
                      <button
                        type="button"
                        onClick={async (e) => {
                          e.stopPropagation();
                          // window.confirm() is blocked in iframe sandboxes (hosting
                          // preview), causing the click to silently no-op. Skip the confirm
                          // and proceed directly — the impersonation banner gives an
                          // immediate "Exit impersonation" path so this is safe.
                          try {
                            const res = await api.post(`/admin/impersonate/${r.user_id}`);
                            const t = res.data?.token;
                            const u = res.data?.user;
                            if (!t || !u) throw new Error("No token/user returned");
                            try { sessionStorage.setItem("admin_original_token", sessionStorage.getItem("mysilo_token") || ""); } catch { /* ignore */ }
                            sessionStorage.setItem("mysilo_token", t);
                            // May 28, 2026 — CRITICAL: also overwrite mysilo_user with the
                            // impersonated user's payload. Without this, DashboardGate
                            // reads the stale super-admin cache and renders the creator
                            // Dashboard even when impersonating a fan. (Randy hit this
                            // bug — saw the creator dashboard while as a fan.)
                            sessionStorage.setItem("mysilo_user", JSON.stringify(u));
                            toast.success(`Now impersonating ${r.email}`);
                            setTimeout(() => { window.location.href = "/dashboard"; }, 400);
                          } catch (e) {
                            toast.error(e?.response?.data?.detail || "Impersonate failed.");
                          }
                        }}
                        data-testid={`impersonate-${r.user_id}`}
                        className="text-[10px] uppercase tracking-[0.15em] px-2 py-1 rounded border hairline text-[#4A5D4E] hover:border-[#4A5D4E] hover:bg-[#F0F1EE]"
                        title="Sign in as this creator (2-hour token)"
                      >
                        Impersonate
                      </button>
                      )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {/* Pager — client-side over the filtered/sorted set. */}
      {!loading && visible.length > 0 && (
        <div className="flex items-center justify-between gap-3 mt-3 text-sm text-[#6B6A68]" data-testid="admin-creator-pagination">
          <span data-testid="admin-creator-showing">
            Showing {(((page - 1) * PAGE_SIZE) + 1).toLocaleString()}–{Math.min(page * PAGE_SIZE, visible.length).toLocaleString()} of {visible.length.toLocaleString()}
          </span>
          <div className="flex items-center gap-2">
            <button type="button" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}
              data-testid="admin-creator-prev"
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border hairline bg-white text-[#1C1B1A] hover:bg-[#F0F1EE] disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-white transition-colors">
              <CaretLeft size={12} weight="bold" /> Prev
            </button>
            <span className="tabular-nums text-xs" data-testid="admin-creator-pageinfo">Page {page} / {totalPages}</span>
            <button type="button" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              data-testid="admin-creator-next"
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border hairline bg-white text-[#1C1B1A] hover:bg-[#F0F1EE] disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-white transition-colors">
              Next <CaretRight size={12} weight="bold" />
            </button>
          </div>
        </div>
      )}
      {/* Oct 2026 (Randy) — two labelled strips. The old single row of six
          pills mixed "who is on their silos" with "who they brought in" and
          the caption underneath promised a nesting the code never did. Press
          a card once to highlight those rows above, twice to show only them,
          a third time to clear. */}
      {selected && (
        <div className="mt-5" data-testid="admin-audience-pills">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
            <div className="text-xs text-[#6B6A68]">
              Audience for <span className="text-[#1C1B1A] font-medium">{selected.email || selected.name || "—"}</span>
              {audience?.silo_count != null && <> · {audience.silo_count} silo{audience.silo_count === 1 ? "" : "s"}</>}
            </div>
            <button type="button" onClick={() => { setSelected(null); setAudience(null); setPill(null); }}
              data-testid="admin-audience-clear"
              className="text-[10px] uppercase tracking-[0.15em] text-[#6B6A68] hover:text-[#1C1B1A] transition-colors">
              Clear
            </button>
          </div>
          {audLoading ? (
            <div className="p-4 text-center text-xs text-[#6B6A68]">Loading…</div>
          ) : (
            <>
              <PillStrip
                title="On their silos"
                note="Fans (total) is the sum of the three cards to its right. Anonymous visitors have no account, so that card has nobody to show."
                pills={AUDIENCE_PILLS} audience={audience} pill={pill} onPress={pressPill}
              />
              <PillStrip
                title="People they brought in"
                note="Bucketed by what each account is TODAY — the same badge the Role column paints. Earning credit is the subset the money engine recognises, so a comped or gifted Creator Pro counts here and still earns nothing."
                pills={REFERRAL_PILLS} audience={audience} pill={pill} onPress={pressPill}
              />
              <div className="text-[10px] text-[#6B6A68] italic mt-2" data-testid="admin-audience-hint">
                Press a card once to highlight those people in the table above · twice to show only them · a third time to clear.
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function PillStrip({ title, note, pills, audience, pill, onPress }) {
  return (
    <div className="mb-3" data-testid={`admin-pill-strip-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="text-[9px] uppercase tracking-[0.22em] text-[#A89360] mb-1.5">{title}</div>
      <div className="grid grid-cols-5 gap-1 sm:gap-2">
        {pills.map((p) => {
          const n = audience ? (audience.counts?.[p.key] ?? 0) : null;
          const stage = pill?.key === p.key ? pill.stage : 0;
          const pressable = p.key !== "anonymous";
          return (
            <button
              key={p.key}
              type="button"
              disabled={!pressable}
              onClick={() => pressable && onPress(p.key)}
              data-testid={`admin-audience-pill-${p.key}`}
              data-stage={stage}
              title={pressable ? "Press: highlight → show only → clear" : "Visitors have no account to list"}
              className={`rounded-md border px-1 py-2 text-center min-w-0 transition-all ${p.cls} ${
                stage ? "ring-2 ring-offset-1 ring-[#C16E5A] scale-[1.02]" : ""
              } ${pressable ? "hover:border-[#C16E5A] cursor-pointer" : "cursor-default opacity-80"}`}
            >
              <div className="text-base sm:text-lg font-serif-display tabular-nums leading-none" data-testid={`admin-audience-count-${p.key}`}>
                {n == null ? "—" : n.toLocaleString()}
              </div>
              <div className="text-[8px] sm:text-[9px] uppercase tracking-[0.08em] mt-1 leading-tight">{p.label}</div>
              <div className="text-[8px] opacity-60 leading-tight hidden sm:block">{stage === 2 ? "showing only" : stage === 1 ? "highlighted" : p.sub}</div>
            </button>
          );
        })}
      </div>
      <div className="text-[9px] text-[#6B6A68] italic mt-1">{note}</div>
    </div>
  );
}

export function ReportModal({ scope, days, onClose, siloFilter = null, onRecallToStaging = null }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterEndpoint, setFilterEndpoint] = useState("");
  const [filterUserEmail, setFilterUserEmail] = useState("");
  const [sortKey, setSortKey] = useState("date_desc");
  const [recallingId, setRecallingId] = useState(null);
  const [stagedNote, setStagedNote] = useState(null); // in-modal "sent to staging" CTA (mobile toasts are off)

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const fromIso = isoDaysAgo(days);
        const url = scope === "admin" ? "/admin/usage" : "/usage/me/entries";
        const params = { from_: fromIso };
        if (filterEndpoint) params.endpoint = filterEndpoint;
        if (siloFilter) params.silo_id = siloFilter;
        const r = await api.get(url, { params });
        if (!cancelled) setEntries(r.data);
      } catch { if (!cancelled) toast.error("Failed to load report"); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [days, scope, filterEndpoint, siloFilter]);

  const recallToStaging = async (row) => {
    if (recallingId) return;
    setRecallingId(row.id);
    try {
      const r = await api.get(`/usage/me/snapshot/${row.id}`);
      const out = r.data?.output_snapshot || "";
      const q = (r.data?.input_snapshot || "").trim();
      if (!out) throw new Error("Empty snapshot.");
      // Jul 2026 (Randy) — carry the QUESTION with the answer into staging.
      const combined = q ? `Q: ${q}\n\nA: ${out}` : out;
      // The snapshot tells us which silo the row belongs to; staging is per-silo.
      const targetSilo = r.data?.silo_id || siloFilter || row.silo_id || null;
      let total;
      if (onRecallToStaging) {
        total = onRecallToStaging(combined);          // Operations tab: also updates its live counter
      } else {
        total = appendToStaging(targetSilo, combined).length;
      }
      // Mobile toasts are hidden platform-wide, so show an in-modal confirmation.
      setStagedNote({ added: combined.length, total: Number(total ?? 0) });
      setTimeout(() => setStagedNote((n) => (n && !n.error ? null : n)), 2600);
    } catch (e) {
      const msg = e?.response?.status === 410
        ? "This entry aged out of the 50-answer buffer."
        : e?.response?.status === 404
          ? "Entry no longer available."
          : "Could not send to staging.";
      setStagedNote({ error: msg });
      setTimeout(() => setStagedNote((n) => (n && n.error ? null : n)), 3200);
    } finally {
      setRecallingId(null);
    }
  };

  const filtered = useMemo(() => {
    let rows = entries;
    if (filterUserEmail) {
      const q = filterUserEmail.toLowerCase();
      rows = rows.filter((r) => (r.user_email || "").toLowerCase().includes(q));
    }
    const sorted = [...rows];
    if (sortKey === "date_desc") sorted.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    else if (sortKey === "date_asc") sorted.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    else if (sortKey === "cost_desc") sorted.sort((a, b) => (b.est_cost_cents || 0) - (a.est_cost_cents || 0));
    else if (sortKey === "cost_asc") sorted.sort((a, b) => (a.est_cost_cents || 0) - (b.est_cost_cents || 0));
    else if (sortKey === "size_desc") sorted.sort((a, b) => ((b.input_chars + b.output_chars) || 0) - ((a.input_chars + a.output_chars) || 0));
    else if (sortKey === "size_asc") sorted.sort((a, b) => ((a.input_chars + a.output_chars) || 0) - ((b.input_chars + b.output_chars) || 0));
    return sorted;
  }, [entries, filterUserEmail, sortKey]);

  const totalCost = filtered.reduce((s, r) => s + (r.est_cost_cents || 0), 0);
  const endpointOptions = useMemo(() => Array.from(new Set(entries.map((e) => e.endpoint))), [entries]);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="usage-report-modal">
      <div className="absolute inset-0 bg-[#1C1B1A]/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 w-[96vw] max-w-[1480px] bg-white border hairline rounded-xl shadow-2xl overflow-hidden flex flex-col max-h-[88dvh]">
        {/* Jul 2026 (Randy) — in-modal "sent to staging" confirmation. Mobile
            sonner toasts are hidden platform-wide, so this pill guarantees the
            visible feedback he asked for. */}
        {stagedNote && (
          <div
            data-testid="staging-cta"
            className={`absolute left-1/2 -translate-x-1/2 top-3 z-30 px-4 py-2 rounded-full shadow-lg text-[13px] font-medium flex items-center gap-2 ${stagedNote.error ? "bg-[#8A2E2E] text-white" : "bg-[#2F6B3A] text-white"}`}
            style={{ animation: "fadeSlideDown .18s ease-out" }}
          >
            {stagedNote.error ? (
              <span>{stagedNote.error}</span>
            ) : (
              <span>✓ Appended to staging · +{Number(stagedNote.added).toLocaleString()} chars</span>
            )}
          </div>
        )}
        <div className="flex items-center justify-between px-6 py-4 border-b hairline shrink-0">
          <div>
            <div className="font-serif-display text-lg leading-none text-[#1C1B1A]">Usage report — last {days} days</div>
            <div className="text-xs text-[#6B6A68] mt-1">{filtered.length} call{filtered.length === 1 ? "" : "s"} · {fmtUSD(totalCost)} total</div>
          </div>
          <button onClick={onClose} className="p-2 rounded-md text-[#6B6A68] hover:bg-[#F0F1EE]" data-testid="usage-report-close"><X size={18} /></button>
        </div>

        <div className="px-6 py-3 border-b hairline bg-[#FDFCF8] flex flex-wrap gap-3 items-center">
          <div className="flex items-center gap-1.5 text-xs text-[#6B6A68]"><Funnel size={12} /> Filter</div>
          <select data-testid="usage-filter-endpoint" value={filterEndpoint} onChange={(e) => setFilterEndpoint(e.target.value)}
            className="text-xs bg-white border hairline rounded-md px-3 py-1.5">
            <option value="">All operations</option>
            {endpointOptions.map((ep) => <option key={ep} value={ep}>{ENDPOINT_LABEL[ep] || ep}</option>)}
          </select>
          {scope === "admin" && (
            <input data-testid="usage-filter-user" value={filterUserEmail} onChange={(e) => setFilterUserEmail(e.target.value)}
              placeholder="Filter by user email…"
              className="text-xs bg-white border hairline rounded-md px-3 py-1.5 flex-1 min-w-[160px] max-w-[260px]" />
          )}
          <div className="ml-auto flex items-center gap-2 text-xs text-[#6B6A68]">
            <span>Sort</span>
            <select data-testid="usage-sort" value={sortKey} onChange={(e) => setSortKey(e.target.value)}
              className="bg-white border hairline rounded-md px-3 py-1.5">
              <option value="date_desc">Date · newest</option>
              <option value="date_asc">Date · oldest</option>
              <option value="cost_desc">Cost · high → low</option>
              <option value="cost_asc">Cost · low → high</option>
              <option value="size_desc">Size · high → low</option>
              <option value="size_asc">Size · low → high</option>
            </select>
          </div>
        </div>

        <div className="overflow-auto flex-1">
          {loading ? (
            <div className="p-12 text-center text-[#6B6A68] text-sm">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center text-[#6B6A68] text-sm">No usage in this window.</div>
          ) : (
            <table className="w-full text-sm min-w-[960px]" data-testid="usage-report-table">
              <thead className="bg-[#FDFCF8] sticky top-0 text-[10px] uppercase tracking-[0.18em] text-[#6B6A68]">
                <tr>
                  <th className="text-left px-4 py-2.5 font-medium">When</th>
                  <th className="text-left px-4 py-2.5 font-medium">Operation</th>
                  {scope === "admin" && <th className="text-left px-4 py-2.5 font-medium">Owner</th>}
                  <th className="text-left px-4 py-2.5 font-medium">Asked by</th>
                  <th className="text-left px-4 py-2.5 font-medium">Silo</th>
                  <th className="text-right px-4 py-2.5 font-medium">
                    {/* Oct 6 2026 — this column has ALWAYS been characters, not
                        tokens. Randy read 800,747 here as a token count and
                        reasonably concluded the cost maths was wrong by a
                        different factor than it was. Labelled honestly now; the
                        real token count sits in the tooltip when the provider
                        gave us one. */}
                    <span className="inline-flex items-center justify-end gap-1 cursor-help" data-testid="usage-in-header"
                      title="Input SIZE IN CHARACTERS of the whole prompt — Tier 1 injection, retrieved chunks, rules, voice specimens and conversation history combined. Hover a row to see the provider's real token count where available.">
                      In
                    </span>
                  </th>
                  <th className="text-right px-4 py-2.5 font-medium">
                    {/* Jul 2026 (Randy) — the clickable-Output → staging feature is
                        now available in EVERY usage report (silo, dashboard,
                        identity pill, admin), so show the Pro tip everywhere. */}
                    <span className="inline-flex items-center justify-end gap-1 cursor-help" data-testid="usage-out-header">
                      Out
                      <InfoTip placement="top-center" width={280} label="Pro tip: clickable Output cells">
                        <strong>Pro tip:</strong> click any Output number to push that question + output to the Workbench staging area. <span className="opacity-80">(your last 50 answers)</span>
                      </InfoTip>
                    </span>
                  </th>
                  <th className="text-right px-4 py-2.5 font-medium">Source</th>
                  <th className="text-right px-4 py-2.5 font-medium">Cost</th>
                  {/* Feb 27, 2026 — Super-admin-only extra column. Shows the
                      PROVIDER-raw cost (what the platform actually paid
                      Anthropic / OpenAI / Google). The existing Cost column
                      shows the marked-up creator charge. Spread = margin. */}
                  {scope === "admin" && <th className="text-right px-4 py-2.5 font-medium">Your cost</th>}
                </tr>
              </thead>
              <tbody className="divide-y hairline">
                {filtered.map((r) => (
                  <tr key={r.id} className="hover:bg-[#FDFCF8]" data-testid={`usage-row-${r.id}`}>
                    <td className="px-4 py-2.5 text-[#6B6A68] whitespace-nowrap">{fmtDate(r.created_at)}</td>
                    <td className="px-4 py-2.5 text-[#1C1B1A]">
                      {VOICE_ENDPOINTS.has(r.endpoint) ? (
                        <span
                          className="underline decoration-dotted decoration-[#9aa39b] underline-offset-2 cursor-help"
                          title="Can be turned OFF in Silo settings."
                          data-testid={`usage-voice-hint-${r.id}`}
                        >
                          {ENDPOINT_LABEL[r.endpoint] || r.endpoint}
                        </span>
                      ) : (ENDPOINT_LABEL[r.endpoint] || r.endpoint)}
                      {/* Iter171 (Randy pt-usage-grounding-globe, Feb 13 2026)
                          — When Google Search Grounding fired on this call
                          (currently only AI Assistant), append a Globe icon
                          so it visually distinguishes grounded vs pure calls.
                          `grounding_used` is set at log time via
                          server.py::log_usage (whitelist at line ~3560) and
                          surfaced on the UsageEntry response model. */}
                      {r.grounding_used && (
                        <span
                          className="ml-1.5 inline-flex items-center text-[#4A5D4E] align-middle"
                          data-testid={`usage-grounding-globe-${r.id}`}
                          title="Google Search grounding fired on this call (adds a per-call surcharge)."
                        >
                          <Globe size={13} weight="duotone" />
                        </span>
                      )}
                      {r.image_count > 1 && (
                        <span className="ml-1.5 text-[10px] font-medium text-[#4A5D4E] bg-[#EEF1ED] rounded px-1.5 py-0.5 whitespace-nowrap" data-testid={`usage-batch-count-${r.id}`} title={`${r.image_count} images made in one batch`}>×{r.image_count}</span>
                      )}
                    </td>
                    {scope === "admin" && <td className="px-4 py-2.5 text-[#1C1B1A] truncate max-w-[160px]" data-testid={`usage-owner-${r.id}`} title={r.user_email || ""}>{r.user_name || r.user_email || "—"}</td>}
                    <td className="px-4 py-2.5" data-testid={`usage-askedby-${r.id}`}>
                      {r.asker_role ? (
                        <span className="inline-flex items-center gap-1.5">
                          <span className={`text-[10px] uppercase tracking-[0.15em] px-1.5 py-0.5 rounded whitespace-nowrap ${askerBadgeFor(r.asker_role).cls}`}>{askerBadgeFor(r.asker_role).label}</span>
                          {r.asker_name && r.asker_role !== "anonymous" && (
                            <span className="text-[11px] text-[#6B6A68] truncate max-w-[110px]" title={r.asker_name}>{r.asker_name}</span>
                          )}
                        </span>
                      ) : (
                        <span className="text-[#6B6A68]">—</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-[#6B6A68] truncate max-w-[200px]">{r.silo_name || "—"}</td>
                    <td className="px-4 py-2.5 text-right text-[#6B6A68]"
                      data-testid={`usage-in-chars-${r.id}`}
                      title={
                        r.input_tokens
                          ? `${(r.input_tokens).toLocaleString()} real input tokens reported by the provider` +
                            (r.cached_input_tokens ? ` · ${(r.cached_input_tokens).toLocaleString()} served from the provider's cache` : " · none served from cache")
                          : "No provider token count for this call — cost approximated from character length"
                      }>{(r.input_chars || 0).toLocaleString()}</td>
                    <td className="px-4 py-2.5 text-right text-[#6B6A68]">
                      {r.has_snapshot ? (
                        <button
                          type="button"
                          onClick={() => recallToStaging(r)}
                          disabled={recallingId === r.id}
                          data-testid={`usage-recall-${r.id}`}
                          title="Click to send this question + output to Workbench staging"
                          className="text-[#4A5D4E] hover:text-[#1C1B1A] hover:underline underline-offset-2 decoration-dotted font-medium disabled:opacity-60"
                        >
                          {recallingId === r.id ? "Sending…" : (r.output_chars || 0).toLocaleString()}
                        </button>
                      ) : (
                        <span className={r.endpoint && ["ask", "workbench-run", "yt-metadata", "yt-transcript", "yt-comments", "yt-library", "chore-plan", "chore-master-file", "buddy-chat", "ytre", "ytre-master-reply", "ytre-suggest-range", "yt-replies"].includes(r.endpoint) ? "text-[#6B6A68] opacity-60" : ""}
                          title={["ask", "workbench-run", "yt-metadata", "yt-transcript", "yt-comments", "yt-library", "chore-plan", "chore-master-file", "buddy-chat", "ytre", "ytre-master-reply", "ytre-suggest-range", "yt-replies"].includes(r.endpoint) ? "Snapshot aged out of the 50-entry buffer." : ""}>
                          {(r.output_chars || 0).toLocaleString()}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <KeySourcePill source={r.key_source} model={r.model_name} markupX={r.markup_x} />
                    </td>
                    <td className="px-4 py-2.5 text-right font-medium text-[#1C1B1A]">
                      {(() => {
                        // Iter172 audit-4 (Randy pt-voucher-drift-audit,
                        // Feb 13, 2026) — the previous blanket FREE rule
                        // painted EVERY BYOK-Flash call FREE regardless
                        // of token count. A 640k-token Flash prompt ate
                        // ~65% of Google's 1M/day free quota in ONE call
                        // yet still showed FREE. Result: Randy's voucher
                        // dropped $0.21 while the Usage Report showed
                        // $0.00 for the same window.
                        //
                        // New rule: the Cost column shows what YOU
                        // (creator) effectively paid the provider — so
                        // always show the estimator's honest number.
                        // Google Flash pricing is cheap ($0.075/M in),
                        // so real Flash calls DO show sub-cent to a-few-
                        // cents values — matches the true drift from
                        // your voucher. The FREE tag now ONLY applies
                        // when the estimator returns 0 cents exactly
                        // (e.g., YouTube Data API pulls, offline
                        // embeddings — truly zero-cost operations).
                        //
                        // See /app/memory/VOUCHER_DRIFT_ENGINE.md for
                        // the follow-up drift reconciliation engine.
                        const est = r.est_cost_cents || 0;
                        if (est === 0) {
                          return <span className="text-[#2F6B3A]">FREE</span>;
                        }
                        // Feb 28, 2026 — Color the cost cell RED for platform
                        // (3×) and GREEN for BYOK (1×) in BOTH admin and
                        // creator scopes. Single visual cue across the table.
                        const src = (r.key_source || "").toLowerCase();
                        const colorClass = src === "byok" || src === "byok-yt" || src === "byok_yt"
                          ? "text-[#2F6B3A]" : "text-[#C16E5A]";
                        // Oct 6 2026 (Randy pt-usage-cost-accuracy) — cost
                        // PROVENANCE. Randy: "Do not silently imply that old
                        // rows have exact token-based costs if only
                        // character-based estimates were recorded." A row
                        // priced from the provider's own token counts is
                        // measured; anything else is an estimate, and rows
                        // written before 2026-10-06 were ALSO priced from a
                        // stale table that under-reported Gemini ~4x. Marked,
                        // never silently mixed.
                        const basis = r.cost_basis || null;
                        const mark = basis === "tokens"
                          ? null
                          : (
                            <span
                              className="ml-1 text-[9px] uppercase tracking-wider text-[#9b9a96] font-normal align-middle"
                              data-testid={`usage-cost-basis-${r.id}`}
                              title={
                                basis === "chars"
                                  ? "ESTIMATE — the provider returned no token counts for this call, so cost was approximated from character length (~4 chars/token)."
                                  : basis === "override"
                                  ? "Fixed cost supplied by the calling feature rather than estimated from tokens."
                                  : "LEGACY ESTIMATE — recorded before Oct 6 2026, from character length and a model price table that under-reported Gemini by roughly 4×. Not comparable with newer rows."
                              }
                            >
                              {basis === "override" ? "set" : "est"}
                            </span>
                          );
                        return (<span className={colorClass}>{fmtUSD(r.est_cost_cents)}{r.image_count > 1 ? <span className="text-[#9b9a96] font-normal"> ×{r.image_count}</span> : null}{mark}</span>);
                      })()}
                    </td>
                    {scope === "admin" && (
                      <td className="px-4 py-2.5 text-right font-medium"
                        data-testid={`usage-raw-cost-${r.id}`}
                        title="Provider-raw cost (Anthropic / OpenAI / Google invoice). $0 on BYOK rows because the creator paid the provider directly.">
                        {(() => {
                          const src = (r.key_source || "").toLowerCase();
                          const model = (r.model_name || "").toLowerCase();
                          const grounded = !!r.grounding_used;
                          // Mirror the Cost cell: Flash on a BYOK key is free
                          // (Google free tier), so your real cost is $0 too —
                          // show FREE instead of the list-price estimate.
                          // Iter171 — but grounding surcharge is real money,
                          // so we skip FREE when grounding fired.
                          if (src === "byok" && model.includes("flash") && !model.includes("image") && !grounded) {
                            return <span className="text-[#2F6B3A]">FREE</span>;
                          }
                          const colorClass = src === "byok" || src === "byok-yt" || src === "byok_yt"
                            ? "text-[#2F6B3A]" : "text-[#C16E5A]";
                          return <span className={colorClass}>{fmtUSD(r.raw_cost_cents || 0)}</span>;
                        })()}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

// Feb 28, 2026 — Map a model id to a short abbreviation for the usage
// report. Falls back to a sensible "Gemini"/"GPT"/"Claude" placeholder
// when we hit a model we haven't catalogued.
function modelAbbrev(model) {
  if (!model) return "—";
  const m = String(model).toLowerCase();
  if (m.startsWith("claude-opus-4-7")) return "CL Op 4.7";
  if (m.startsWith("claude-opus-4-6")) return "CL Op 4.6";
  if (m.startsWith("claude-opus")) return "CL Op";
  if (m.startsWith("claude-sonnet-4-6")) return "CL Son 4.6";
  if (m.startsWith("claude-sonnet-4-5")) return "CL Son 4.5";
  if (m.startsWith("claude-sonnet")) return "CL Son";
  if (m.startsWith("claude-haiku")) return "CL Hk";
  if (m.startsWith("claude")) return "Claude";
  if (m.startsWith("gpt-5.2")) return "GPT 5.2";
  if (m.startsWith("gpt-5.1")) return "GPT 5.1";
  if (m.startsWith("gpt-5")) return "GPT 5";
  if (m.startsWith("gpt-4o-mini")) return "GPT 4o m";
  if (m.startsWith("gpt-4o")) return "GPT 4o";
  if (m.startsWith("gpt")) return "GPT";
  if (m.startsWith("gemini-3.1-pro")) return "Gem 3.1 Pro";
  if (m.startsWith("gemini-3-flash")) return "Gem 3 Fl";
  if (m.startsWith("gemini-2.5-pro")) return "Gem Pro";
  if (m.startsWith("gemini-2.5-flash")) return "Gem Fl";
  if (m.startsWith("gemini")) return "Gemini";
  return model.length > 12 ? `${model.slice(0, 12)}…` : model;
}

// iter-31-P2b: clickable key-source pill. Navigates to Operations tab and scrolls
// to the right card. Works whether the user is already on Operations or not —
// the tab switch is handled by the hash and a small sessionStorage flag the
// Dashboard watches on mount.
//
// Feb 28, 2026 — Replaced the generic "Platform" / "BYOK" labels with the
// per-row model abbreviation. The chip's color carries the cost-source signal
// instead: RED when the creator paid 3× through the platform reservoir,
// GREEN when the call landed on a BYOK direct connection at 1× raw cost.
function KeySourcePill({ source, model, markupX }) {
  const nav = useNavigate();
  const target = keySourceTargetFor(source);
  const lowerSrc = (source || "").toLowerCase();
  const lowerModel = (model || "").toLowerCase();
  // Gemini *Flash text* is free on a BYOK key, but `gemini-2.5-flash-image`
  // (Nano Banana) is a PAID image model — never mark image models free.
  const isFlashFree = lowerSrc === "byok" && lowerModel.includes("flash") && !lowerModel.includes("image");
  const isByok = lowerSrc === "byok" || lowerSrc === "byok-yt" || lowerSrc === "byok_yt";
  // Oct 2026 — a platform-key row billed at 1× was funded by credit the
  // platform gave away (trial/comp) or bought during a trial. Showing it at the
  // normal 3× label would misreport what the creator was actually charged.
  const atCost = !isByok && Number(markupX) === 1;
  const displayLabel = isFlashFree
    ? `${modelAbbrev(model)} · FREE`
    : (atCost ? `${modelAbbrev(model)} · 1×` : modelAbbrev(model));
  const tone = isFlashFree
    ? "bg-[#E6F2E6] text-[#2F6B3A] hover:bg-[#D4EAD4]"
    : (isByok
        ? "bg-[#E8EBE5] text-[#2F6B3A] hover:bg-[#D4DDD2]"
        : (atCost
            ? "bg-[#E8EBE5] text-[#4A5D4E] hover:bg-[#DCE3D8]"
            : "bg-[#FDF1ED] text-[#C16E5A] hover:bg-[#FCE5DC]"));

  const onClick = (e) => {
    e.preventDefault();
    if (!target) return;
    try {
      sessionStorage.setItem("dashboard_tab", "ops");
      sessionStorage.setItem("dashboard_scroll_to", target.hash);
    } catch { /* ignore */ }
    const alreadyOnDashboard = window.location.pathname === "/dashboard";
    if (alreadyOnDashboard) {
      window.dispatchEvent(new CustomEvent("dashboard-scroll-to", { detail: { hash: target.hash, tab: "ops" } }));
    } else {
      nav(`/dashboard${target.hash}`);
    }
  };

  const titleText = isByok
    ? `${model || "—"} · BYOK direct · 1× cost`
    : (atCost
        ? `${model || "—"} · Platform key · 1× at cost (trial or gifted credit)`
        : `${model || "—"} · Platform reservoir · 3× cost`);

  if (!target) {
    return (
      <span className={`text-[10px] uppercase tracking-[0.15em] px-1.5 py-0.5 rounded ${tone}`}
        title={titleText}
        data-testid={`key-source-pill-${lowerSrc}`}>
        {displayLabel}
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      title={titleText}
      data-testid={`key-source-pill-${lowerSrc}`}
      className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] px-1.5 py-0.5 rounded transition-colors ${tone}`}
    >
      {displayLabel}
      <ArrowSquareOut size={9} weight="bold" className="opacity-70" />
    </button>
  );
}

